/* PUSH THIS OFFER TO HER CRM.
 *
 * The button and the sentence she reads before it happens. Everything about
 * what actually goes over lives server-side (api/_lib/ghlJobs/offer.js, through api/ghl.js) — this
 * file knows only that pressing it asks what WOULD happen, says so, and sends
 * nothing until she agrees.
 *
 * WHY IT ASKS FIRST. Her rule, 2026-09-22: "it will need a warning that says
 * if you push this to GHL it will overwrite anything with the same name or
 * create a new one." Brill wins on a clash — she decided that — and her
 * products are wired into landing pages that take money, so an overwrite is
 * something a buyer sees the moment it lands. A generic disclaimer is a thing
 * people learn to click through; this one READS her CRM first and names what
 * is actually there.
 *
 * ONE DESTINATION, NO DROPDOWN. "We could give more options in the future" —
 * so the button says where it is going rather than offering a menu of one. It
 * grows a caret the day there is a second place to send it.
 *
 * AND A SHORT LABEL ON A PHONE, her note: "push to GoHighLevel is a long
 * name". The control says the verb, the dialog says the detail — the same rule
 * the drafting screen's arrow and its bubble-and-eye follow.
 */
import { useState } from "react";
import { useSession } from "@clerk/clerk-react";
import { Upload } from "lucide-react";
import { ConfirmDialog } from "../ConfirmDialog";
import { B, btnGhost } from "./tokens";
import { useTwoPane } from "./ui";

/* The sentence, built from what the server found. Every branch says what will
   happen to the thing she can see, never what the code will do. */
export function planWords(plan) {
  const kind = plan.assumedKind ? `${plan.productType} (Brill's guess — pick a Kind to be sure)` : plan.productType;
  /* FREE IS ITS OWN SENTENCE. "Its price goes over as $0" is true and reads
     like a mistake; what she needs to know is that the $0 is deliberate and
     what it buys her — a product with no price at all cannot sit in a funnel. */
  const price = !plan.price
    ? `No price goes over — ${plan.priceSkipped}.`
    : plan.price === "$0"
      ? "It is Free, so it goes over as a $0 product — which is what lets it sit in a funnel or an order form."
      : `Its price goes over as ${plan.price}.`;

  if (plan.action === "update") {
    return [
      `There is already a product called "${plan.name}" in your CRM, and Brill will overwrite its name and description.`,
      "If it is connected to a funnel or a landing page, buyers see that change immediately.",
      `${price} A price is added rather than replaced, so check the funnel is pointing at the right one.`,
    ].join(" ");
  }
  return [
    `Nothing in your CRM is called "${plan.name}", so this creates a new product as a ${kind}.`,
    price,
  ].join(" ");
}

/**
 * @param offer     the row being looked at — only its id and name are used
 * @param onDone    called after a successful push, for the read-out
 */
export function PushToCrm({ offer, onDone }) {
  const { session } = useSession();
  const twoPane = useTwoPane();
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState(null);      // what the server says would happen
  const [problem, setProblem] = useState("");
  const [done, setDone] = useState("");

  /* A new offer has no row to read yet, and an unnamed one has nothing GHL
     will accept — the button is not offered rather than offered and refused. */
  if (!offer?.id || offer.isNew || !String(offer.name || "").trim()) return null;

  const call = async (preview) => {
    const token = await session?.getToken();
    const res = await fetch("/api/ghl", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ job: "offer", offerId: offer.id, preview }),
    });
    return res.json();
  };

  const ask = async () => {
    setBusy(true); setProblem(""); setDone("");
    try {
      const r = await call(true);
      if (!r.ok) setProblem(r.error || "That didn't work.");
      else setPlan(r.plan);
    } catch (err) {
      setProblem(`Could not reach your CRM — ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    setPlan(null); setBusy(true); setProblem("");
    try {
      const r = await call(false);
      if (!r.ok) { setProblem(r.error || "That didn't work."); return; }
      /* What landed AND what did not. "Pushed" over a product whose price was
         refused is the silence rule 1 exists to stop. */
      setDone(r.priceFailed
        ? `${r.done === "updated" ? "Updated" : "Created"} — but the price did not go: ${r.priceFailed}`
        : r.priced
          ? `${r.done === "updated" ? "Updated" : "Created"} in your CRM, priced ${r.priced}`
          : `${r.done === "updated" ? "Updated" : "Created"} in your CRM${r.priceSkipped ? ` — no price, ${r.priceSkipped}` : ""}`);
      onDone?.(r);
    } catch (err) {
      setProblem(`Could not reach your CRM — ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button onClick={ask} disabled={busy}
        title="Push this offer to GoHighLevel"
        style={{ ...btnGhost, display: "inline-flex", alignItems: "center", gap: 7, opacity: busy ? 0.6 : 1 }}>
        <Upload size={14} />
        {busy ? "Checking…" : twoPane ? "Push to GoHighLevel" : "Push"}
      </button>

      {/* Beside the button, not in a banner at the top: she is looking here. */}
      {done && <span style={{ fontSize: 12.5, color: B.teal }}>{done}</span>}
      {problem && <span style={{ fontSize: 12.5, color: B.amber }}>{problem}</span>}

      {plan && (
        <ConfirmDialog
          title={`${plan.name} → GoHighLevel`}
          message={planWords(plan)}
          /* Danger only when something already there is about to change. A
             create is not a destructive act and should not wear the colour
             that means one. */
          danger={plan.action === "update"}
          confirmLabel={plan.action === "update" ? "Overwrite it" : "Create it"}
          onConfirm={send}
          onCancel={() => setPlan(null)}
        />
      )}
    </>
  );
}
